import React from 'react'
import { Row, SpreadsheetCellShell, SpreadsheetCellViewValue, TableCellDragHandle } from '@ztechapps/webui.data-tools'
import type { RowProps } from '@ztechapps/webui.data-tools'
import { BusyButton } from '@ztechapps/webui.button'
import { TextInput } from '@ztechapps/webui.input'
import type { MetadataListItem } from './MetadataSchema'
import { useMetadataContext } from './Context'
import { CommonMetadataSelect } from './CommonMetadataSelect'
import { UnitSelect } from './UnitSelect'
import { GridColumn } from './GridColumn'
import { resolveValueDisplay } from './resolveValueDisplay'
import { hasSelectValue } from './hasSelectValue'

/**
 * Spreadsheet row for the Specifications grid. Receives the `RowProps` shape from
 * `@ztechapps/webui.data-tools` (row data spread + `_config`); `_config.index` is the canonical
 * row index used by `getCellProps(rowIndex, col)`.
 *
 * Row data is read from `useMetadataContext().list[rowIndex]` rather than from the spread `props`.
 * React installs a non-enumerable `key` getter on the props object that warns on access — reading
 * from canonical state avoids that.
 *
 * Each cell renders in one of two modes:
 *   - VIEW: a `<SpreadsheetCellViewValue>` text node. Cheap render.
 *   - EDIT: the active input (`CommonMetadataSelect` / `ValueInput` / `UnitSelect`). Only the
 *     active cell renders its input at any time, so the grid never mounts dozens of
 *     `react-select` instances at once.
 *
 * Drag wiring: the drag-handle cell uses `<TableCellDragHandle>` which reads `useHandleProps()`
 * from `<HandlePropsProvider>`. The `<Table row={ListRow}>` path supplies that provider via
 * `MemoizedRow`.
 */
export type ListRowProps = RowProps & MetadataListItem

export const ListRow = (props: ListRowProps) => {
  const { list, onChange, onDelete, metadataSchema, hasRole, getCellProps, exitEditMode, selectCell } =
    useMetadataContext()

  const rowIndex = props._config?.index ?? 0
  const item = list[rowIndex]
  // `currentMetadataSchema.input` is replaced at runtime with the actual React component
  // (`TextInput`, `NumberInput`, `MetadataValueSelect`, …) by `useProductMetadata.ts:44`. The
  // compile-time type is the enum string — runtime/type mismatch handled with an `any` cast.
  const ValueInput: any = item?.currentMetadataSchema?.input ?? TextInput

  const nameCell = getCellProps(rowIndex, GridColumn.Name)
  const valueCell = getCellProps(rowIndex, GridColumn.Value)
  const unitCell = getCellProps(rowIndex, GridColumn.Unit)

  const focusCell = (col: number) => () => selectCell(rowIndex, col)

  // `<TextInput>` / `<NumberInput>` default `onFocus` to `(e) => e.target.select()` — which
  // highlights the entire value and means the next keystroke replaces everything. Sheets behavior
  // is: cursor lands at end, typing appends. Since we pre-injected the trigger char via
  // `onEnterEditCallback` (replacing the prior value), placing the cursor at end lets the user
  // continue typing the rest of the value. `setSelectionRange` throws on Chrome for
  // `type=number`; guard for it.
  const placeCursorAtEnd = (e: React.FocusEvent<HTMLInputElement>) => {
    const target = e.currentTarget
    const len = target.value.length
    try {
      target.setSelectionRange(len, len)
    } catch {
      // Number inputs in Chromium don't support setSelectionRange; browser default is end-of-value
      // on programmatic focus anyway.
    }
  }

  return (
    <Row {...props}>
      {/* Drag handle is mouse-only — `tabIndex={-1}` keeps it out of the keyboard tab order.
          `TableCellDragHandle` renders its own `<td>` and forwards `...rest`, so `tabIndex` goes
          directly here (a wrapping `<td>` would be invalid `<td>`-in-`<td>` nesting). */}
      <TableCellDragHandle disabled={!hasRole} tabIndex={-1} />

      {/* Name (Keys column) */}
      <SpreadsheetCellShell
        isActive={nameCell.isActive}
        isEditing={nameCell.isEditing}
        tabIndex={nameCell.tabIndex}
        className="p-0"
      >
        {nameCell.isEditing ? (
          <CommonMetadataSelect
            value={item?.key}
            onChange={(e) => {
              const value = e?.target?.value ?? e?.value
              onChange(value, rowIndex, 'key', { currentMetadataSchema: metadataSchema?.[value] })
            }}
            spreadsheetMode={{
              onCommit: exitEditMode,
              defaultMenuIsOpen: true,
              defaultInputValue: nameCell.initialEditChar ?? undefined,
            }}
          />
        ) : (
          <SpreadsheetCellViewValue
            onClick={focusCell(GridColumn.Name)}
            text={item?.currentMetadataSchema?.name ?? item?.key ?? ''}
          />
        )}
      </SpreadsheetCellShell>

      {/* Value */}
      <SpreadsheetCellShell
        isActive={valueCell.isActive}
        isEditing={valueCell.isEditing}
        tabIndex={valueCell.tabIndex}
        className="p-0"
      >
        {valueCell.isEditing ? (
          <ValueInput
            disabled={!hasRole}
            autoFocus
            onFocus={placeCursorAtEnd}
            value={item?.value}
            onChange={(e: any) => {
              const value = e?.target?.value ?? e?.value ?? e
              onChange(value, rowIndex, 'value')
            }}
            {...item?.currentMetadataSchema?.inputProps}
            // `spreadsheetMode` reaches select editors only — `NumberInput` forwards unknown
            // props to the DOM `<input>`, which would log a React unknown-prop warning.
            {...(hasSelectValue(item)
              ? {
                  spreadsheetMode: {
                    onCommit: exitEditMode,
                    defaultMenuIsOpen: true,
                    defaultInputValue: valueCell.initialEditChar ?? undefined,
                  },
                }
              : {})}
          />
        ) : (
          <SpreadsheetCellViewValue
            onClick={focusCell(GridColumn.Value)}
            text={resolveValueDisplay(item?.value, item?.currentMetadataSchema)}
          />
        )}
      </SpreadsheetCellShell>

      {/* Unit */}
      <SpreadsheetCellShell
        isActive={unitCell.isActive}
        isEditing={unitCell.isEditing}
        tabIndex={unitCell.tabIndex}
        className="p-0"
      >
        {unitCell.isEditing ? (
          <UnitSelect
            value={item?.unit}
            onChange={(e) => {
              onChange(e?.value, rowIndex, 'unit')
            }}
            inputName={item?.currentMetadataSchema?.inputName}
            spreadsheetMode={{
              onCommit: exitEditMode,
              defaultMenuIsOpen: true,
              defaultInputValue: unitCell.initialEditChar ?? undefined,
            }}
          />
        ) : (
          <SpreadsheetCellViewValue onClick={focusCell(GridColumn.Unit)} text={item?.unit ?? ''} />
        )}
      </SpreadsheetCellShell>

      {/* Trash icon — mouse fallback. `tabIndex={-1}` keeps it out of the keyboard tab order.
          `style={{ width: 1 }}` is the standard HTML-table shrink-to-fit trick: with `table-layout:
          auto`, a cell explicitly sized at 1px becomes "as narrow as its content allows," matching
          the drag-handle column's natural width on the left edge. Without it the auto layout
          distributes the table's residual width into this column and the trash icon sits in a
          ~150px wide cell. */}
      <td className="p-0 text-center" tabIndex={-1} style={{ width: 1 }}>
        <BusyButton
          disabled={!hasRole}
          disabledReason={hasRole ? undefined : 'You need the product.canEdit permission.'}
          bsStyle={'link'}
          icon={'trash'}
          onClick={() => {
            onDelete?.(rowIndex)
          }}
        />
      </td>
    </Row>
  )
}
